import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import axios from "axios";
import { useSession } from "@/services/auth0/session";

// Types
export type AnnouncementType = "INFO" | "WARNING" | "ERROR";

export interface Announcement {
  id: string;
  title: string;
  message: string;
  type: AnnouncementType;
  linkUrl: string | null;
  linkText: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface AnnouncementsResponse {
  data: Announcement[];
}

interface AnnouncementResponse {
  data: Announcement;
}

// Validation schema
export const announcementSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  message: z.string().min(1, "Message is required").max(2000),
  type: z.enum(["INFO", "WARNING", "ERROR"]).default("INFO"),
  linkUrl: z
    .string()
    .url("Invalid URL")
    .optional()
    .nullable()
    .or(z.literal("")),
  linkText: z.string().max(100).optional().nullable(),
  isActive: z.boolean().default(false),
  sortOrder: z.number().int().default(0),
});

export type AnnouncementFormValues = z.infer<typeof announcementSchema>;

// Hooks
export const useAnnouncements = () => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["announcements"],
    queryFn: async () => {
      const response = await axios.get<AnnouncementsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/admin/announcements`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    },
    enabled: !!token,
  });
};

export const useCreateAnnouncement = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (data: AnnouncementFormValues) => {
      // Clean up empty string for linkUrl
      const payload = {
        ...data,
        linkUrl: data.linkUrl || null,
        linkText: data.linkText || null,
      };
      const response = await axios.post<AnnouncementResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/admin/announcements`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["announcements"] });
      options?.onSuccess?.();
    },
  });
};

export const useUpdateAnnouncement = (
  announcementId: string,
  options?: { onSuccess?: () => void }
) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (data: Partial<AnnouncementFormValues>) => {
      // Clean up empty string for linkUrl
      const payload = {
        ...data,
        linkUrl: data.linkUrl === "" ? null : data.linkUrl,
        linkText: data.linkText === "" ? null : data.linkText,
      };
      const response = await axios.patch<AnnouncementResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/admin/announcements/${announcementId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["announcements"] });
      options?.onSuccess?.();
    },
  });
};

export const useDeleteAnnouncement = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (announcementId: string) => {
      const response = await axios.delete<{ message: string }>(
        `${import.meta.env.VITE_API_URL}/api/v2/admin/announcements/${announcementId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["announcements"] });
      options?.onSuccess?.();
    },
  });
};
